import axios from 'axios';
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5154';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor ───────────────────────────────────────────────────────
// Attaches JWT + session token to EVERY request automatically
api.interceptors.request.use(
  (config) => {
    const token        = localStorage.getItem('divyayoga_token');
    const sessionToken = localStorage.getItem('divyayoga_session');

    if (token)        config.headers['Authorization']   = `Bearer ${token}`;
    if (sessionToken) config.headers['X-Session-Token'] = sessionToken;

    return config;
  },
  (error) => Promise.reject(error)
);

let isRedirecting = false;

// ── Response interceptor ──────────────────────────────────────────────────────
// Handles 401 globally — clears auth and redirects to login (deduplicated)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const currentPath = window.location.pathname;

    const isPublicPath =
      currentPath === '/login' ||
      currentPath === '/register' ||
      currentPath === '/forgot-password' ||
      currentPath === '/reset-password' ||
      currentPath === '/account-deactivated';

    const isPublicRoute =
      url.includes('/auth/user/login') ||
      url.includes('/auth/user/register') ||
      url.includes('/auth/send-registration-otp') ||
      url.includes('/auth/verify-registration-otp') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password');

    const token = localStorage.getItem('divyayoga_token');

    if (error.response?.status === 401 && !isPublicRoute && !isPublicPath && token) {
      if (!isRedirecting) {
        isRedirecting = true;
        toast.error("Session expired. Please login again.");
        localStorage.removeItem('divyayoga_token');
        localStorage.removeItem('divyayoga_user');
        localStorage.removeItem('divyayoga_role');
        localStorage.removeItem('divyayoga_session');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      }
    }

    return Promise.reject(error);
  }
);

// ─── AUTH ENDPOINTS ───────────────────────────────────────────────────────────
export const authAPI = {
  sendOtp:        (email)      => api.post('/auth/send-registration-otp', { email }),
  verifyOtp:      (email, otp) => api.post('/auth/verify-registration-otp', { email, otp }),
  register:       (data)       => api.post('/auth/user/register', data),
  login:          (data)       => api.post('/auth/user/login', data),
  adminLogin:     (data)       => api.post('/auth/admin/login', data),
  forgotPassword: (email)      => api.post('/auth/forgot-password', { email }),
  resetPassword:  (data)       => api.post('/auth/reset-password', data),
};

// ─── USER ENDPOINTS ───────────────────────────────────────────────────────────
export const userAPI = {
  // Profile   fetch from DB, not localStorage
  getProfile:     ()       => api.get('/user/profile'),
  updateProfile:  (data)   => api.put('/user/profile', data),
  getUserSubscription: () => api.get('/user/subscription'),

  // Password
  changePassword: (data)   => api.put('/user/change-password', data),

  // Account
  deleteAccount:  (password) => api.delete('/user/account', { data: { password } }),
  deactivateAccount: () => api.put('/user/deactivate'),

  reactivateAccount: () => api.put('/user/reactivate'),

  deleteAccountPermanent: (password) =>  api.delete('/user/account/permanent', {
    data: { password }
  }),
  
  // Sessions
  getSessions:       ()    => api.get('/user/sessions'),
  revokeSession:     (id)  => api.delete(`/user/sessions/${id}`),
  revokeAllSessions: ()    => api.delete('/user/sessions'),
  heartbeat:         ()    => api.put('/user/sessions/heartbeat'),

  // Attendance (Check-In / Check-Out)
  getTodayAttendanceStatus: () => api.get('/user/attendance/today-status'),
  checkIn:                  (location) => api.post(location ? `/user/attendance/check-in?location=${encodeURIComponent(location)}` : '/user/attendance/check-in'),
  checkOut:                 () => api.post('/user/attendance/check-out'),
  getAttendanceHistory:     () => api.get('/user/attendance/history'),

  // Mindfulness Tracking
  getMindfulness:           () => api.get('/user/mindfulness'),
  logMindfulness:           (data) => api.post('/user/mindfulness/log', data),

  // Subscription & Packages
  getPublicPackages:        () => api.get('/user/packages'),
  checkoutSubscription:     (packageId) => api.post('/user/checkout', { packageId }),
};

// ─── BLOG ENDPOINTS ───────────────────────────────────────────────────────────
export const blogAPI = {
  getBlogs:   ()         => api.get('/blog'),
  createBlog: (data)     => api.post('/blog', data),
  updateBlog: (id, data) => api.put(`/blog/${id}`, data),
  deleteBlog: (id)       => api.delete(`/blog/${id}`),
};

// ─── PRACTICE VIDEOS ENDPOINTS ────────────────────────────────────────────────
export const practiceVideoAPI = {
  getVideos:   ()         => api.get('/practicevideo'),
  createVideo: (data)     => api.post('/practicevideo', data),
  updateVideo: (id, data) => api.put(`/practicevideo/${id}`, data),
  deleteVideo: (id)       => api.delete(`/practicevideo/${id}`),
  bulkImportVideos: (data) => api.post('/practicevideo/bulk', data),
  interactVideo: (id, incView = false, rating = null, like = false) => {
    let query = `?incView=${incView}&like=${like}`;
    if (rating !== null) {
      query += `&rating=${rating}`;
    }
    return api.put(`/practicevideo/${id}/interact${query}`);
  },
  getProgress: () => api.get('/practicevideo/progress'),
  updateProgress: (data) => api.post('/practicevideo/progress', data),
  getBookmarks: () => api.get('/practicevideo/bookmarks'),
  toggleBookmark: (videoId) => api.post('/practicevideo/bookmarks', { videoId }),
  uploadVideo: (formData) => api.post('/practicevideo/upload-video', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadThumbnail: (formData) => api.post('/practicevideo/upload-thumbnail', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// ─── LIVE SESSIONS ENDPOINTS ──────────────────────────────────────────────────
export const liveSessionAPI = {
  getSessions:    ()         => api.get('/livesession'),
  getSession:     (id)       => api.get(`/livesession/${id}`),
  createSession:  (data)     => api.post('/livesession', data),
  uploadVideo:    (formData) => api.post('/livesession/upload-video', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  checkIn: (id, payloadOrLocation) => {
    if (typeof payloadOrLocation === 'object' && payloadOrLocation !== null) {
      return api.post(`/livesession/${id}/check-in`, payloadOrLocation);
    }
    return api.post(payloadOrLocation ? `/livesession/${id}/check-in?location=${encodeURIComponent(payloadOrLocation)}` : `/livesession/${id}/check-in`);
  },
  getProgress:    ()         => api.get('/livesession/user-progress'),
  postSignal:     (id, data) => api.post(`/livesession/${id}/signal`, data),
  getSignals:     (id, target) => api.get(`/livesession/${id}/signal?target=${target}`),
  updateSession:  (id, data) => api.put(`/livesession/${id}`, data),
  deleteSession:  (id)       => api.delete(`/livesession/${id}`),
  clearAllSessions: ()       => api.post('/livesession/admin/clear-all'),
  cancelBooking:  (id)       => api.post(`/livesession/${id}/cancel-booking`),
  rescheduleBooking: (data)  => api.post('/livesession/reschedule', data),
  setRecordingUrl: (id, data)=> api.post(`/livesession/${id}/recording`, data),
  deleteRecording: (id)      => api.delete(`/livesession/${id}/recording`),
  getLiveKitToken: (id)      => api.get(`/livesession/${id}/livekit-token`),
};

// ─── NOTIFICATION ENDPOINTS ───────────────────────────────────────────────────
export const notificationAPI = {
  getNotifications: ()       => api.get('/notification'),
  markAsRead:       (id)     => api.post(`/notification/${id}/read`),
  getAdminNotificationHistory: () => api.get('/notification/history'),
  sendNotification: (data)   => api.post('/notification/send', data),
  getNotificationRecipients: (id) => api.get(`/notification/${id}/recipients`),
  deleteNotification: (id)   => api.delete(`/notification/${id}`),
  clearAllNotifications: ()  => api.delete('/notification/clear-all'),
  deleteAllNotifications: () => api.delete('/notification/delete-all'),
  markAllAsRead: ()          => api.put('/notification/read-all'),

  // Web Push Additive Endpoints
  getVapidPublicKey: () => api.get('/notification/vapid-public-key'),
  subscribePush: (data) => api.post('/notification/subscribe-push', data),
  sendTestPush: () => api.post('/notification/test-push'),
};

// ─── DIET PLAN ENDPOINTS ──────────────────────────────────────────────────────
export const dietPlanAPI = {
  getUserPreference: () => api.get('/dietplan/preference'),
  saveUserPreference: (data) => api.post('/dietplan/preference', data),
  getMyPlan: (dietType = '', region = '', calorieGoal = '') => {
    let url = '/dietplan/my-plan';
    const params = [];
    if (dietType) params.push(`dietType=${encodeURIComponent(dietType)}`);
    if (region) params.push(`region=${encodeURIComponent(region)}`);
    if (calorieGoal) params.push(`calorieGoal=${encodeURIComponent(calorieGoal)}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return api.get(url);
  },
  toggleMealLog: (data) => api.post('/dietplan/toggle-meal', data),
  getAdminPlans: () => api.get('/dietplan/admin/plans'),
  saveAdminPlan: (data) => api.post('/dietplan/admin/plans', data),
  deleteAdminPlan: (id) => api.delete(`/dietplan/admin/plans/${id}`),
  cleanupDuplicates: () => api.post('/dietplan/admin/cleanup-duplicates'),
  clearAllPlans: () => api.post('/dietplan/admin/clear-all'),
  getUserTracking: () => api.get('/dietplan/admin/user-tracking'),
  generateAiPlan: (data) => api.post('/dietplan/admin/generate-ai-plan', data),
  getDietQuota: () => api.get('/dietplan/quota'),
  generateMyPlan: (data) => api.post('/dietplan/generate-my-plan', data),
};


// ─── GOAL ENDPOINTS ────────────────────────────────────────────────────────────
export const goalsAPI = {
  getGoals: () => api.get('/goal'),
  syncGoals: (data) => api.post('/goal/sync', data),
  createGoal: (data) => api.post('/goal', data),
  updateGoal: (id, data) => api.put(`/goal/${id}`, data),
  deleteGoal: (id) => api.delete(`/goal/${id}`),
  logProgress: (id, amount) => api.post(`/goal/${id}/log`, { amount }),
};

// ─── ACHIEVEMENT ENDPOINTS ────────────────────────────────────────────────────
export const achievementAPI = {
  getSummary: () => api.get('/achievement/summary'),
};

// ─── CONTACT / USER QUERY ENDPOINTS ───────────────────────────────────────────
export const contactAPI = {
  submitQuery: (data) => api.post('/contact/submit', data),
};

// ─── COMMUNITY ENDPOINTS ──────────────────────────────────────────────────────
export const communityAPI = {
  getPosts: (params = {}) => {
    const query = new URLSearchParams();
    if (params.category && params.category !== 'All') query.append('category', params.category);
    if (params.search) query.append('search', params.search);
    if (params.sort) query.append('sort', params.sort);
    if (params.page) query.append('page', params.page);
    if (params.pageSize) query.append('pageSize', params.pageSize);
    const qs = query.toString();
    return api.get(`/community/posts${qs ? `?${qs}` : ''}`);
  },
  getPost: (id) => api.get(`/community/posts/${id}`),
  createPost: (data) => api.post('/community/posts', data),
  deletePost: (id) => api.delete(`/community/posts/${id}`),
  toggleLike: (id) => api.post(`/community/posts/${id}/like`),
  getComments: (id) => api.get(`/community/posts/${id}/comments`),
  addComment: (id, data) => api.post(`/community/posts/${id}/comments`, data),
  deleteComment: (id) => api.delete(`/community/comments/${id}`),
  getStats: () => api.get('/community/stats'),
};

export default api;